import { LoadingState } from "@repo/app-types/client/client";
import {
  type ProjectTemplate,
  ProjectType,
  getQuestionsForProjectType,
} from "@repo/app-types/constants/projectTypeToDefaultQuestions";
import type { Company } from "@repo/app-types/entities/company";
import {
  InterviewAgentType,
  ProjectMode,
} from "@repo/app-types/entities/project";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Input } from "@repo/ui/Inputs/input";
import { Label } from "@repo/ui/Label/label";
import { Modal } from "@repo/ui/Modal/modal";
import { ModalHeader } from "@repo/ui/Modal/modalHeader";
import { SimpleSelect } from "@repo/ui/Select/simpleSelect";
import { Size } from "@repo/ui/helpers/helpers";
import { useState } from "react";
import { PhoneNumberSelect } from "../../phoneNumbers/phoneNumberSelect.js";
import { InterviewAgentTypeSetting } from "../projectSettings/interviewAgentTypeSetting.js";

const projectModeOptions = [
  { label: "AI Survey", value: ProjectMode.SURVEY },
  { label: "Voice agent", value: ProjectMode.VOICE_AGENT },
];

interface CreateProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  name: string;
  template?: ProjectTemplate;
  onNameChange: (name: string) => void;
  mode: ProjectMode | null;
  onModeChange: (mode: ProjectMode) => void;
  onClickCreate: ({
    jobDescription,
    interviewAgentType,
    phoneNumberId,
  }: {
    jobDescription?: string;
    interviewAgentType?: InterviewAgentType;
    phoneNumberId?: string;
  }) => void;
  company: Company;
  loadingState: LoadingState;
}

export const CreateProjectModal = ({
  isOpen,
  onClose,
  name,
  template,
  mode,
  onModeChange,
  onNameChange,
  onClickCreate,
  company,
  loadingState,
}: CreateProjectModalProps) => {
  // Normally this setting is predetermined by the project type, but for "Custom" projects it
  // can be modified
  const [interviewAgentType, setInterviewAgentType] =
    useState<InterviewAgentType>(InterviewAgentType.GenericInterviewer);

  const [selectedPhoneNumberId, setSelectedPhoneNumberId] = useState<string>();

  const renderFields = () => {
    if (!template) {
      return null;
    }

    const questions = getQuestionsForProjectType(
      template.type,
      company.name,
      mode === ProjectMode.VOICE_AGENT,
    );

    return (
      <>
        <div className="mt-6 mb-2">
          <Input
            autoFocus
            label="Project name"
            onChange={(evt) => {
              onNameChange(evt.target.value);
            }}
            placeholder="Feedback interviews"
            value={name}
            maxLength={100}
          />
        </div>

        <div className="mt-2 mb-1">
          <Label size={Size.SMALL}>Project mode</Label>
        </div>
        <div className="max-w-[200px]">
          <SimpleSelect
            value={mode ?? ProjectMode.SURVEY}
            onChange={onModeChange}
            options={projectModeOptions}
          />
        </div>
        <div className="mt-2 text-sm text-muted-foreground">
          Use AI Survey mode for more structure and quantitative data, and Voice
          Agent mode for dynamic phone-based conversations.
        </div>
        {mode === ProjectMode.VOICE_AGENT &&
          template.type === ProjectType.Custom && (
            <div className="mt-2 mb-1">
              <InterviewAgentTypeSetting
                interviewAgentType={interviewAgentType}
                onChange={(newInterviewAgentType) => {
                  setInterviewAgentType(newInterviewAgentType);
                }}
              />
            </div>
          )}
        {questions.length ? (
          <div className="mt-4 text-sm">
            <label className="mb-1 block text-sm leading-6 font-medium text-foreground">
              Questions template
            </label>
            <div>
              {questions.map((question, index) => {
                return (
                  <div
                    key={`template-question-${index}`}
                    className="flex flex-row space-x-2"
                  >
                    <div>{`${index + 1}.`}</div>
                    <div className="mb-1">{question.question}</div>
                  </div>
                );
              })}
            </div>
            <div className="mt-2 text-muted-foreground">
              {generateQuestionsTemplateHelperText(template.type)}
            </div>
          </div>
        ) : null}
        {mode === ProjectMode.VOICE_AGENT && (
          <PhoneNumberSelect
            className="mt-4"
            onChange={setSelectedPhoneNumberId}
            phoneNumberId={selectedPhoneNumberId}
          />
        )}
      </>
    );
  };

  const renderButtons = () => {
    return (
      <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
        <div className="ml-3">
          <Button
            isDisabled={!name}
            isLoading={loadingState === LoadingState.LOADING}
            onClick={() => {
              onClickCreate({
                interviewAgentType,
                phoneNumberId: selectedPhoneNumberId,
              });
            }}
            variant={ButtonVariant.Primary}
          >
            Create project
          </Button>
        </div>
        <Button onClick={onClose} variant={ButtonVariant.Secondary}>
          Cancel
        </Button>
      </div>
    );
  };

  return (
    <Modal isOpen={isOpen} onCancel={onClose}>
      <ModalHeader>New project</ModalHeader>
      {renderFields()}
      {renderButtons()}
    </Modal>
  );
};

function generateQuestionsTemplateHelperText(type: ProjectType) {
  switch (type) {
    case ProjectType.CandidateScreen:
      return "You'll be able to edit these questions later and generate questions based on a job description.";

    case ProjectType.BrandPerception:
    case ProjectType.Discovery:
    case ProjectType.ConceptTesting:
    case ProjectType.Win:
    case ProjectType.Loss:
    case ProjectType.SuperchargedNPS:
    case ProjectType.Churn:
      return "You'll be able to edit these questions later or generate questions from a research discussion guide.";

    default:
      return "You can always edit these questions later.";
  }
}
